import { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Routes from "models/routes";
import { resetPassword } from "utils/user/passwordResetApi";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import LanguageMenu from "components/common/ui/LanguageMenu";
import {
  LanguageMenuCorner,
  LoginButton,
  LoginMessageText,
  LoginPageContainer,
  LoginPageLink,
  LoginTextField,
  LoginTitle,
  LoginTitleSub,
} from "components/login/ui/loginPageStyles";

const MIN_PASSWORD_LENGTH = 8;

interface ResetPasswordPageProps {}

const ResetPasswordPage: FC<ResetPasswordPageProps> = () => {
  const { t } = useTranslation();
  const promptSnackBar = usePromptSnackBar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [succeeded, setSucceeded] = useState<boolean>(false);

  const handleNewPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setNewPassword(event.target.value);
      setNewPasswordError(null);
    },
    [],
  );

  const handleConfirmPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setConfirmPassword(event.target.value);
      setConfirmPasswordError(null);
    },
    [],
  );

  const handleResetClick = useCallback(() => {
    if (token === null) {
      return;
    }
    let valid = true;
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setNewPasswordError(
        t("Password must be at least {{count}} characters.", {
          count: MIN_PASSWORD_LENGTH,
        }),
      );
      valid = false;
    }
    if (confirmPassword !== newPassword) {
      setConfirmPasswordError(t("Passwords do not match."));
      valid = false;
    }
    if (!valid) {
      return;
    }
    setSaving(true);
    resetPassword({ token, newPassword })
      .then(() => {
        setSucceeded(true);
      })
      .catch(() => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: t(
            "Failed to reset the password. The link may be invalid or expired.",
          ),
          severity: "error",
          autoHeightDuration: 5000,
        });
      })
      .finally(() => {
        setSaving(false);
      });
  }, [confirmPassword, newPassword, promptSnackBar, t, token]);

  const handleKeypress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleResetClick();
      }
    },
    [handleResetClick],
  );

  const handleGoToSignInClick = useCallback(() => {
    navigate(Routes.LOGIN);
  }, [navigate]);

  if (token === null) {
    return (
      <LoginPageContainer>
        <LanguageMenuCorner>
          <LanguageMenu />
        </LanguageMenuCorner>
        <LoginTitle>Chef Jasper</LoginTitle>
        <LoginTitleSub>{t("Set a new password")}</LoginTitleSub>
        <LoginMessageText>
          {t("This password reset link is invalid. Please request a new one.")}
        </LoginMessageText>
        <LoginPageLink to={Routes.FORGOT_PASSWORD}>
          {t("Request a new link")}
        </LoginPageLink>
      </LoginPageContainer>
    );
  }

  return (
    <LoginPageContainer>
      <LanguageMenuCorner>
        <LanguageMenu />
      </LanguageMenuCorner>
      <LoginTitle>Chef Jasper</LoginTitle>
      <LoginTitleSub>{t("Set a new password")}</LoginTitleSub>
      {succeeded ? (
        <>
          <LoginMessageText>
            {t("Your password has been updated.")}
          </LoginMessageText>
          <LoginButton
            text={t("Go to sign in")}
            positive
            onClick={handleGoToSignInClick}
          />
        </>
      ) : (
        <>
          <LoginTextField
            label={t("New password")}
            value={newPassword}
            type="password"
            error={newPasswordError !== null}
            helperText={newPasswordError}
            onChange={handleNewPasswordChange}
            onKeyDown={handleKeypress}
          />
          <LoginTextField
            label={t("Confirm password")}
            value={confirmPassword}
            type="password"
            error={confirmPasswordError !== null}
            helperText={confirmPasswordError}
            onChange={handleConfirmPasswordChange}
            onKeyDown={handleKeypress}
          />
          <LoginButton
            text={t("Reset password")}
            positive
            disabled={saving}
            onClick={handleResetClick}
          />
        </>
      )}
    </LoginPageContainer>
  );
};

export default ResetPasswordPage;
